import { useState, useEffect } from 'react'
import { useAuth } from './stores/useAuth'
import { useSettings } from './stores/useSettings'
import { realtimeManager } from './services/realtime'
import { initNudgeScheduler, stopNudgeScheduler } from './services/nudges'
import LoginPage from './pages/Login'
import ResetPasswordPage from './pages/ResetPassword'
import DashboardPage from './pages/Dashboard'
import InboxPage from './pages/Inbox'
import TriagePage from './pages/Triage'
import ReviewPage from './pages/Review'
import SquadPage from './pages/Squad'
import SettingsPage from './pages/Settings'
import { BottomNav } from './components/BottomNav'

type Page = 'dashboard' | 'inbox' | 'triage' | 'review' | 'squad' | 'settings'

function App() {
  const { init, user, loading, passwordRecovery } = useAuth()
  const { load: loadSettings } = useSettings()
  const [currentPage, setCurrentPage] = useState<Page>('dashboard')

  useEffect(() => {
    const unsub = init()
    return () => {
      unsub()
    }
  }, [init])

  useEffect(() => {
    if (user) {
      loadSettings()
      realtimeManager.init()
      initNudgeScheduler()
    } else {
      realtimeManager.cleanup()
      stopNudgeScheduler()
    }
  }, [user, loadSettings])

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--bg, #0f172a)',
        }}
      >
        <div className="logo-mark animate-pulse">L</div>
        <div className="spinner" style={{ marginTop: 'var(--lg)' }}></div>
      </div>
    )
  }

  // Must be checked before the `!user` branch: Supabase establishes a session
  // for password-recovery links too, so `user` is already set at this point.
  // Without this check, recovery would drop the user straight onto the
  // dashboard instead of the "set new password" screen.
  if (passwordRecovery) {
    return <ResetPasswordPage />
  }

  if (!user) {
    return <LoginPage />
  }

  return (
    <>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {currentPage === 'dashboard' && <DashboardPage />}
        {currentPage === 'inbox' && <InboxPage onNavigate={(p: string) => setCurrentPage(p as Page)} />}
        {currentPage === 'triage' && <TriagePage onNavigate={(p: string) => setCurrentPage(p as Page)} />}
        {currentPage === 'review' && <ReviewPage />}
        {currentPage === 'squad' && <SquadPage />}
        {currentPage === 'settings' && <SettingsPage />}
      </div>
      <BottomNav page={currentPage} onNavigate={setCurrentPage} />
    </>
  )
}

export default App
